/**
 * Log Service
 * Captures debug logs when developer mode is enabled
 * Stores logs persistently in localStorage with size limits
 */

import { storageService } from './storage.service';

interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  category: string;
  message: string;
  data?: any;
}

class LogService {
  private static readonly MAX_LOGS = 500; // Keep last 500 logs
  private static readonly MAX_SIZE_BYTES = 1024 * 1024; // 1 MB limit
  private static readonly STORAGE_KEY = 'fish_diary_debug_logs';
  private static readonly SETTINGS_CHECK_INTERVAL = 5000; // Check settings every 5 seconds
  
  private isDeveloperMode: boolean = false;
  private settingsCheckTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.initializeSettingsCheck();
  }

  /**
   * Periodically check if developer mode is enabled
   * This allows logs to start/stop automatically when settings change
   */
  private async initializeSettingsCheck() {
    await this.checkDeveloperMode();
    
    this.settingsCheckTimer = setInterval(async () => {
      await this.checkDeveloperMode();
    }, LogService.SETTINGS_CHECK_INTERVAL);
  }

  private async checkDeveloperMode() {
    try {
      const settings = await storageService.getSettings();
      this.isDeveloperMode = settings.developerMode ?? false;
    } catch {
      this.isDeveloperMode = false;
    }
  }

  /**
   * Log an info message
   */
  async info(category: string, message: string, data?: any) {
    await this.log('info', category, message, data);
    // Always console.log regardless of dev mode for critical info
    console.log(`[${category}] ${message}`, data || '');
  }

  /**
   * Log a warning message
   */
  async warn(category: string, message: string, data?: any) {
    await this.log('warn', category, message, data);
    console.warn(`[${category}] ${message}`, data || '');
  }

  /**
   * Log an error message
   */
  async error(category: string, message: string, data?: any) {
    await this.log('error', category, message, data);
    console.error(`[${category}] ${message}`, data || '');
  }

  /**
   * Internal log method that writes to storage if dev mode is enabled
   */
  private async log(level: LogEntry['level'], category: string, message: string, data?: any) {
    // Only persist logs if developer mode is enabled
    if (!this.isDeveloperMode) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data: data ? this.sanitizeData(data) : undefined,
    };

    try {
      const logs = await this.getLogs();
      logs.push(entry);

      // Keep only the last MAX_LOGS entries
      if (logs.length > LogService.MAX_LOGS) {
        logs.splice(0, logs.length - LogService.MAX_LOGS);
      }

      // Check size and trim if necessary
      let logsJson = JSON.stringify(logs);
      const sizeBytes = new Blob([logsJson]).size;
      
      // If exceeds max size, remove oldest logs until under limit
      while (sizeBytes > LogService.MAX_SIZE_BYTES && logs.length > 10) {
        logs.splice(0, 10); // Remove 10 oldest logs at a time
        logsJson = JSON.stringify(logs);
        const newSize = new Blob([logsJson]).size;
        if (newSize >= sizeBytes) break; // Safety check to avoid infinite loop
      }

      localStorage.setItem(LogService.STORAGE_KEY, logsJson);
    } catch (error) {
      // If storage fails, fail silently to avoid breaking the app
      console.error('Failed to persist log:', error);
    }
  }

  /**
   * Sanitize data to prevent circular references and reduce size
   */
  private sanitizeData(data: any): any {
    if (data === null || data === undefined) return data;
    
    try {
      // For errors, extract useful info
      if (data instanceof Error) {
        return {
          name: data.name,
          message: data.message,
          stack: data.stack,
        };
      }

      // For objects, try to serialize
      if (typeof data === 'object') {
        // Limit depth and array sizes to prevent huge logs
        return JSON.parse(JSON.stringify(data, this.getCircularReplacer(), 2));
      }

      return data;
    } catch {
      return String(data);
    }
  }

  /**
   * Circular reference replacer for JSON.stringify
   */
  private getCircularReplacer() {
    const seen = new WeakSet();
    return (key: string, value: any) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular]';
        }
        seen.add(value);
      }
      return value;
    };
  }

  /**
   * Get all stored logs
   */
  async getLogs(): Promise<LogEntry[]> {
    try {
      const stored = localStorage.getItem(LogService.STORAGE_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }

  /**
   * Clear all stored logs
   */
  async clearLogs(): Promise<void> {
    try {
      localStorage.removeItem(LogService.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear logs:', error);
    }
  }

  /**
   * Export logs as JSON file
   */
  async exportLogs(): Promise<string> {
    const logs = await this.getLogs();
    return JSON.stringify(logs, null, 2);
  }

  /**
   * Get log size in bytes
   */
  async getLogSize(): Promise<{ bytes: number; kb: number; mb: number }> {
    try {
      const stored = localStorage.getItem(LogService.STORAGE_KEY);
      if (!stored) return { bytes: 0, kb: 0, mb: 0 };
      
      const bytes = new Blob([stored]).size;
      const kb = bytes / 1024;
      const mb = kb / 1024;
      
      return { bytes, kb, mb };
    } catch {
      return { bytes: 0, kb: 0, mb: 0 };
    }
  }

  /**
   * Get log statistics
   */
  async getStats(): Promise<{
    totalLogs: number;
    infoCount: number;
    warnCount: number;
    errorCount: number;
    oldestLog?: string;
    newestLog?: string;
    categories: string[];
    sizeKB: number;
    maxSizeKB: number;
  }> {
    const logs = await this.getLogs();
    const size = await this.getLogSize();
    
    const stats = {
      totalLogs: logs.length,
      infoCount: logs.filter(l => l.level === 'info').length,
      warnCount: logs.filter(l => l.level === 'warn').length,
      errorCount: logs.filter(l => l.level === 'error').length,
      oldestLog: logs.length > 0 ? logs[0].timestamp : undefined,
      newestLog: logs.length > 0 ? logs[logs.length - 1].timestamp : undefined,
      categories: Array.from(new Set(logs.map(l => l.category))),
      sizeKB: Math.round(size.kb * 100) / 100,
      maxSizeKB: Math.round((LogService.MAX_SIZE_BYTES / 1024) * 100) / 100,
    };

    return stats;
  }

  /**
   * Cleanup on destroy
   */
  destroy() {
    if (this.settingsCheckTimer) {
      clearInterval(this.settingsCheckTimer);
      this.settingsCheckTimer = null;
    }
  }
}

export const logService = new LogService();
export default LogService;
